import { type ReactNode, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Button, Checkbox, Text, Paper } from "@mantine/core";
import {
  IconCrosshair,
  IconSquareDashed,
  IconUser,
  IconPointer,
  IconZoomIn,
  IconZoomOut,
  IconRefresh,
  IconSun,
  IconEye,
  IconLayoutGrid,
  IconList,
  IconTrash,
  IconArrowBackUp,
  IconReplace,
  IconPlus,
  IconHelpCircle,
} from "@tabler/icons-react";

const I = ({ children, color }: { children: ReactNode; color?: string }) => (
  <span style={{ display: "inline-flex", verticalAlign: "middle", color }}>
    {children}
  </span>
);

interface TrainingStep {
  title: string;
  description: ReactNode;
  targetSelector: string | null;
  tooltipPosition: "top" | "bottom" | "left" | "right" | "center";
}

// Fallback selectors: if the primary target isn't visible, try these instead
const FALLBACK_SELECTORS: Record<string, string> = {
  '[data-training="planogram-list"]': '[data-training="right-panel"]',
  '[data-training="planogram-viewer"]': '[data-training="right-panel"]',
  '[data-training="view-toggle"]': '[data-training="right-panel"]',
};

const TRAINING_STEPS: TrainingStep[] = [
  {
    title: "Welcome to Coverage Task",
    description:
      "Your job is to identify out-of-stock items on store shelves.\n\nYou'll review auto-detected boxes, draw new ones, select the missing product from a planogram, and categorize each detection before submitting.",
    targetSelector: null,
    tooltipPosition: "center",
  },
  {
    title: "Drawing Classes",
    description: (
      <>
        Pick a class before drawing:
        <br />• <I color="#228be6"><IconCrosshair size={14} /></I> OOS Box (blue) — mark where a product is missing
        <br />• <I color="#7950f2"><IconSquareDashed size={14} /></I> Empty Area (purple) — mark empty shelf space with no expected product
        <br />• <I color="#fd7e14"><IconUser size={14} /></I> Person (orange) — mark unblurred people that need flagging
      </>
    ),
    targetSelector: '[data-training="sidebar-classes"]',
    tooltipPosition: "right",
  },
  {
    title: "Navigation Tools",
    description: (
      <>
        Below the classes you'll find navigation tools:
        <br />• <I><IconPointer size={14} /></I> Cursor — pan around the image without drawing
        <br />• <I><IconZoomIn size={14} /></I> / <I><IconZoomOut size={14} /></I> Zoom In / Out — magnify the shelf
        <br />• <I><IconRefresh size={14} /></I> Reset View — restore zoom and brightness to defaults
        <br />• <I><IconSun size={14} /></I> Brightness — adjust image brightness with a slider
        <br />• <I><IconEye size={14} /></I> Eye — toggle all boxes on or off
      </>
    ),
    targetSelector: '[data-training="sidebar-tools"]',
    tooltipPosition: "right",
  },
  {
    title: "Draw on the Shelf",
    description:
      "Select a drawing class, then click and drag on the shelf image to draw a box. Each box gets a number.\n\nZoom in for precision — boxes snap to where you draw them.",
    targetSelector: '[data-training="shelf-image"]',
    tooltipPosition: "right",
  },
  {
    title: "Auto-Detected Boxes",
    description:
      'Some boxes are already placed by the system. These "auto" detections start as Unassigned — you\'ll need to categorize each one as OOS Confirmed or Empty Area before you can submit.',
    targetSelector: '[data-training="shelf-image"]',
    tooltipPosition: "right",
  },
  {
    title: "Planogram & Detections Toggle",
    description: (
      <>
        The right panel has two views — switch between them with these buttons:
        <br />• <I><IconLayoutGrid size={14} /></I> Grid — Planogram view (select products)
        <br />• <I><IconList size={14} /></I> List — Detections view (categorize boxes)
      </>
    ),
    targetSelector: '[data-training="view-toggle"]',
    tooltipPosition: "left",
  },
  {
    title: "The Planogram Viewer",
    description:
      "This shows the store's planogram — a diagram of where products should be on the shelf.\n\nWhen you draw an OOS box, this view opens automatically. Click on the product that's missing to assign it to your box.",
    targetSelector: '[data-training="planogram-viewer"]',
    tooltipPosition: "left",
  },
  {
    title: "Categorize Detections",
    description:
      "Switch to the Detections view to see all boxes organized into sections:\n• OOS Confirmed — boxes with a missing product identified\n• Empty Area — empty shelf space, no product expected\n• Unassigned — boxes that still need categorizing\n\nDrag and drop boxes between sections to recategorize them.",
    targetSelector: '[data-training="right-panel"]',
    tooltipPosition: "left",
  },
  {
    title: "Detection Actions",
    description: (
      <>
        Each detection row has actions on the right:
        <br />• <I><IconEye size={14} /></I> Eye — show or hide the box on the shelf image
        <br />• <I><IconTrash size={14} /></I> Trash — delete a box you don't need
        <br />• <I><IconArrowBackUp size={14} /></I> Undo — restore a deleted box
        <br />
        <br />Click a row to select it on the image. OOS boxes without a product show a "MISSING ITEM" badge — click to assign one.
      </>
    ),
    targetSelector: '[data-training="right-panel"]',
    tooltipPosition: "left",
  },
  {
    title: "Multiple Planograms",
    description: (
      <>
        Some tasks have multiple planogram sections. OOS detections are grouped by which planogram they belong to.
        <br />
        <br />Click a planogram tab to switch between them. Use <I><IconReplace size={14} /></I> "Replace" to swap a planogram or <I><IconPlus size={14} /></I> "Add Planogram" to include a new one.
      </>
    ),
    targetSelector: '[data-training="planogram-list"]',
    tooltipPosition: "left",
  },
  {
    title: "Planogram Search Modal",
    description:
      "When you click Replace or Add Planogram, a search modal opens. It has two search modes:\n• Planograms — search by planogram name\n• Products — search by product name to find which planogram contains it\n\nSelect a planogram from the results and click Submit to assign it.",
    targetSelector: ".planogram-search-modal",
    tooltipPosition: "center",
  },
  {
    title: "Review & Submit",
    description:
      'Before submitting, make sure:\n• Every OOS box has a product selected (no "MISSING ITEM" badges)\n• All detections are categorized (no Unassigned boxes)\n• Boxes are in the correct planogram section\n\nThe Submit button stays disabled until all requirements are met. Click it when you\'re ready!',
    targetSelector: '[data-training="top-bar-actions"]',
    tooltipPosition: "bottom",
  },
  {
    title: "Need Help Later?",
    description: (
      <>
        You can reopen this walkthrough anytime by clicking the <I><IconHelpCircle size={14} /></I> help icon next to the "Out of Stock" title in the top bar.
      </>
    ),
    targetSelector: null,
    tooltipPosition: "center",
  },
];

const TOOLTIP_MAX_WIDTH = 360;
const SPOTLIGHT_PADDING = 8;
const TOOLTIP_GAP = 16;

function getSpotlightClipPath(rect: DOMRect, padding = SPOTLIGHT_PADDING): string {
  const top = rect.top - padding;
  const left = rect.left - padding;
  const right = rect.right + padding;
  const bottom = rect.bottom + padding;
  return `polygon(
    0% 0%, 100% 0%, 100% 100%, 0% 100%, 0% 0%,
    ${left}px ${top}px, ${left}px ${bottom}px, ${right}px ${bottom}px, ${right}px ${top}px, ${left}px ${top}px
  )`;
}

const VIEWPORT_MARGIN = 12;

function computeTooltipStyle(
  position: TrainingStep["tooltipPosition"],
  rect: DOMRect | null,
  tooltipEl: HTMLElement | null,
): React.CSSProperties {
  if (position === "center" || !rect) {
    return {
      position: "fixed",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      maxWidth: TOOLTIP_MAX_WIDTH,
    };
  }

  const pad = SPOTLIGHT_PADDING;
  const gap = TOOLTIP_GAP;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = tooltipEl?.offsetWidth ?? TOOLTIP_MAX_WIDTH;
  const th = tooltipEl?.offsetHeight ?? 200;
  const m = VIEWPORT_MARGIN;

  let top: number;
  let left: number;

  switch (position) {
    case "right": {
      left = rect.right + pad + gap;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    }
    case "left": {
      left = rect.left - pad - gap - tw;
      top = rect.top + rect.height / 2 - th / 2;
      break;
    }
    case "bottom": {
      top = rect.bottom + pad + gap;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    }
    case "top": {
      top = rect.top - pad - gap - th;
      left = rect.left + rect.width / 2 - tw / 2;
      break;
    }
  }

  // Clamp within viewport
  top = Math.max(m, Math.min(top, vh - th - m));
  left = Math.max(m, Math.min(left, vw - tw - m));

  return {
    position: "fixed",
    maxWidth: TOOLTIP_MAX_WIDTH,
    top,
    left,
  };
}

interface CoverageTrainingOverlayProps {
  active: boolean;
  onClose: () => void;
  onStepChange?: (stepIndex: number) => void;
}

const STORAGE_KEY = "coverage-training-dismissed";

export const CoverageTrainingOverlay = ({
  active,
  onClose,
  onStepChange,
}: CoverageTrainingOverlayProps) => {
  const [activeStep, setActiveStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState<DOMRect | null>(null);
  const [dontShowAgain, setDontShowAgain] = useState(
    () => localStorage.getItem(STORAGE_KEY) === "1",
  );
  const rafRef = useRef<number>(0);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState(0);

  const step = TRAINING_STEPS[activeStep];

  // Re-render once after tooltip mounts so clamping uses real dimensions
  useLayoutEffect(() => {
    if (tooltipRef.current) {
      forceUpdate((n) => n + 1);
    }
  }, [activeStep]);

  // Fire onStepChange on mount for step 0
  const mountedRef = useRef(false);
  useEffect(() => {
    if (active && !mountedRef.current) {
      mountedRef.current = true;
      onStepChange?.(0);
    }
    if (!active) {
      mountedRef.current = false;
    }
  }, [active, onStepChange]);

  const updateRect = useCallback(() => {
    const currentStep = TRAINING_STEPS[activeStep];
    if (!currentStep?.targetSelector) {
      setSpotlightRect(null);
      return;
    }

    let el = document.querySelector(currentStep.targetSelector);

    // Fallback: if primary target not found, try fallback selector
    if (!el && FALLBACK_SELECTORS[currentStep.targetSelector]) {
      el = document.querySelector(FALLBACK_SELECTORS[currentStep.targetSelector]);
    }

    if (el) {
      setSpotlightRect(el.getBoundingClientRect());
    } else {
      setSpotlightRect(null);
    }
  }, [activeStep]);

  // Update rect on step change and window resize
  useEffect(() => {
    if (!active) return;
    // Delay rect measurement slightly to allow UI state changes (e.g. modal opening) to render
    const timer = setTimeout(updateRect, 50);

    const handleResize = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(updateRect);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("resize", handleResize);
      cancelAnimationFrame(rafRef.current);
    };
  }, [active, updateRect]);

  const handleClose = useCallback(() => {
    if (dontShowAgain) {
      localStorage.setItem(STORAGE_KEY, "1");
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
    setActiveStep(0);
    onClose();
  }, [dontShowAgain, onClose]);

  const handleNext = useCallback(() => {
    if (activeStep < TRAINING_STEPS.length - 1) {
      const nextStep = activeStep + 1;
      setActiveStep(nextStep);
      onStepChange?.(nextStep);
    } else {
      handleClose();
    }
  }, [activeStep, handleClose, onStepChange]);

  const handleBack = useCallback(() => {
    if (activeStep > 0) {
      const prevStep = activeStep - 1;
      setActiveStep(prevStep);
      onStepChange?.(prevStep);
    }
  }, [activeStep, onStepChange]);

  if (!active) return null;

  const isFirst = activeStep === 0;
  const isLast = activeStep === TRAINING_STEPS.length - 1;
  const hasSpotlight = step.targetSelector !== null && spotlightRect !== null;

  return (
    <div className="fixed inset-0 z-[10001]">
      {/* Overlay with spotlight cutout */}
      <div
        className="absolute inset-0 z-[10000]"
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          ...(hasSpotlight
            ? { clipPath: getSpotlightClipPath(spotlightRect) }
            : {}),
        }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* Tooltip */}
      <Paper
        ref={tooltipRef}
        shadow="lg"
        p="md"
        radius="md"
        style={computeTooltipStyle(step.tooltipPosition, spotlightRect, tooltipRef.current)}
        className="z-[10002]"
      >
        <Text fw={700} size="md" mb={4}>
          {step.title}
        </Text>
        <div
          className="text-sm text-gray-500 mb-3"
          style={{ whiteSpace: "pre-line" }}
        >
          {step.description}
        </div>
        <Text size="xs" c="dimmed" mb="sm">
          Step {activeStep + 1} of {TRAINING_STEPS.length}
        </Text>

        <div className="flex items-center justify-between gap-2">
          <Checkbox
            size="xs"
            label="Don't show again"
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.currentTarget.checked)}
          />
          <div className="flex items-center gap-2">
            <Button variant="subtle" size="xs" onClick={handleClose}>
              Skip
            </Button>
            {!isFirst && (
              <Button variant="default" size="xs" onClick={handleBack}>
                Back
              </Button>
            )}
            <Button size="xs" onClick={handleNext}>
              {isLast ? "Got it!" : "Next"}
            </Button>
          </div>
        </div>
      </Paper>
    </div>
  );
};
